import {ellipsis} from 'polished';
import React, {ReactElement} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {CardGroupModel} from '../../../../models/api/cardGroupModel';
import {OverflowWithCount} from '../../overflow/overflowWithCount';
import {getCellBackgroundColor, getCellIndexStyles} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Constants.
 */

const maxCardGroupsHidden = 99;

/*
 * Styles.
 */

const StyledCardGroupCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

interface StyledCardGroupCellPillProps {
  $isSelected: boolean;
}

const StyledCardGroupCellPill = styled.div<StyledCardGroupCellPillProps>`
  padding: 4px 6px 4px 6px;
  border-radius: 10px;
  background-color: ${(p) => p.theme.greys.shade10};
  border: 1px solid ${(p) => p.theme.greys.shade10};
  margin-right: 4px;
  display: flex;
`;

const StyledCardGroupName = styled.div`
  line-height: 12px;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  ${ellipsis()}
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
`;

const StyledCardGroupCellPillWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  width: inherit;
  padding-right: 8px;
`;

const StyledOverflowDiv = styled.div`
  line-height: 19px;
  padding-right: 3px;
  min-width: 30px;
`;

/*
 * Component.
 */

export const CardGroupCell = (props: TableCellProps<ReadonlyArray<CardGroupModel>>): ReactElement => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  return (
    <TableCell<ReadonlyArray<CardGroupModel>>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={(cardGroups) => (
        <StyledCardGroupCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledCardGroupCellPillWrapperDiv>
            <OverflowWithCount<CardGroupModel>
              elements={cardGroups}
              renderElement={renderElement}
              renderOverflowIndicator={renderOverflowIndicator}
            />
          </StyledCardGroupCellPillWrapperDiv>
        </StyledCardGroupCellContainerDiv>
      )}
    />
  );
};

/*
 * Helpers.
 */

function renderElement(cardGroup: CardGroupModel, index: number) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledCardGroupCellPill key={cardGroup.id} $isSelected={false}>
          <StyledCardGroupName>{cardGroup.name}</StyledCardGroupName>
          {isTeammateNamespace(cardGroup.namespace) && (
            <StyledIcon name="lockFilledNewTiny" color={theme.greys.shade70} />
          )}
        </StyledCardGroupCellPill>
      )}
    </UserThemeConsumer>
  );
}

function renderOverflowIndicator(overflowCount: number) {
  const displayNumber = overflowCount > maxCardGroupsHidden ? maxCardGroupsHidden : overflowCount;
  return <StyledOverflowDiv>+{displayNumber}</StyledOverflowDiv>;
}
